/* Кто может повторить движения (§20.1 п. 5, §4.4): «Вы» и вариант «Вы», близкие, персонажи каталога. Один запрос на владельца и пресет;
   ряд, пример «после», голос героя и хвост читают одни и те же записи. Своих картинок и имён у клиента нет.
   Картинка персонажа в каталоге — ключ хранилища live-avatar/…, а не адрес: её отдаёт авторизованный BFF
   (GET /app/catalog/avatars/{id}/image) в общий кэш картинок студии, как у витрины и других рядов «Кто в кадре». */
import { faceCatalog } from '../../api/avatar-catalog';
import { useEffect, useMemo, useRef, useState } from 'react';
import { endpoints } from '../../api';
import type { UserAvatar } from '../../api/people';
import { uiText, useAccountLocale } from '../../i18n';
import type { CircleFormState } from '../studio/circle/CircleFieldInput';
import { leaseArtwork } from '../studio/studio-artwork-cache';
import { motionPolicy } from './motion-model';

export interface MotionChoice {
  value: string;
  kind: 'me' | 'avatar' | 'person' | 'char';
  name: string;
  image: string | null;
  disabled: boolean;
  /** Почему нельзя выбрать (питомец) */
  reason?: string;
  /** Близкий без фото: выбрать можно, но заказ ждёт фото (жёлтая точка, «Добавьте фото») */
  needsPhoto?: boolean;
  female?: boolean;
  /** Голос героя: у персонажа — его голос, у варианта «Вы» — голос варианта */
  voiceId: number | null;
  /** Персонаж каталога: id и версия карточки — ключ его картинки в общем кэше студии */
  id?: number;
  version?: number;
}
/** Сколько портретов держит экран, как у витрины (use-studio-artwork): ряд (персонажи входа — первыми) и начало листа «Все»;
    выбранный персонаж — всегда, даже дальше этого числа */
const ART = 24;
const isArtKey = (value: string | null): value is string => !!value?.startsWith('live-avatar/');
const female = (value: string | null | undefined) => !!value && /^(f|female|ж|жен)/i.test(value);

export function useMotionCast(state: CircleFormState, avatar: UserAvatar | null, first: number[] = []) {
  const [choices, setChoices] = useState<MotionChoice[] | null>(null),
    [error, setError] = useState(false),
    [attempt, setAttempt] = useState(0);
  const locale = useAccountLocale() === 'ru' ? 'ru' : 'en';
  const policy = state.form ? motionPolicy(state.form) : null;
  const latest = useRef(state);
  useEffect(() => {
    latest.current = state;
  });
  const firstKey = first.join(',');
  useEffect(() => {
    let active = true;
    const controller = new AbortController();
    void Promise.all([endpoints.people.self(), endpoints.people.list(), endpoints.avatarCatalog.list(locale, controller.signal)])
      .then(([self, people, full]) => {
        /* реальные люди каталога (D-270) движения не повторяют: они только во вкладке «Актёры» «Озвучки» */
        const catalog = faceCatalog(full);
        if (!active) return;
        const order = firstKey ? firstKey.split(',').map(Number) : [];
        const chars = [...catalog.avatars].sort((a, b) => {
          const ia = order.indexOf(a.id),
            ib = order.indexOf(b.id);
          return (ia < 0 ? 1e6 : ia) - (ib < 0 ? 1e6 : ib);
        });
        const values: MotionChoice[] = [
          { value: 'self', kind: 'me', name: uiText('Вы'), disabled: !self.ready, image: self.photos?.[0]?.url ?? null, voiceId: null },
          ...people.items.map(v => ({
            value: `person:${v.id}`,
            kind: 'person' as const,
            name: v.name,
            disabled: v.is_pet,
            reason: v.is_pet ? uiText('не повторит движения: нужен человек') : undefined,
            needsPhoto: !v.is_pet && !v.photos?.length,
            female: female(v.gender),
            image: v.photos?.[0]?.url ?? null,
            voiceId: null,
          })),
          ...chars.map(v => ({
            value: `character:${v.id}`,
            kind: 'char' as const,
            name: v.full_name || v.name,
            disabled: false,
            female: female(v.sex),
            image: v.image_url || null,
            voiceId: v.default_voice_id ?? v.voice_ids?.[0] ?? null,
            id: v.id,
            version: v.version,
          })),
        ];
        if (avatar?.status === 'ready')
          values.unshift({
            value: `user_avatar:${avatar.id}`,
            kind: 'avatar',
            name: avatar.variant_name,
            disabled: false,
            image: avatar.image_url,
            voiceId: avatar.voice_id,
          });
        setChoices(values);
        setError(false);
        const current = latest.current;
        const p = current.form ? motionPolicy(current.form) : null;
        const def =
          typeof p?.default_cast === 'string'
            ? p.default_cast
            : p?.preset === 'dance'
              ? 'self'
              : (values.find(v => v.kind === 'char' && !v.disabled)?.value ?? '');
        if (!current.locked && !current.inputs.cast && !current.inputs.photo && values.some(v => v.value === def && !v.disabled))
          current.setInputs(v => (v.cast || v.photo ? v : { ...v, cast: def }));
      })
      .catch(() => {
        if (active) setError(true);
      });
    return () => {
      active = false;
      controller.abort();
    };
    // Загрузка общая для одного владельца и пресета, а не по запросу на каждый портрет.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.ownerId, locale, attempt, policy?.preset, avatar?.id, avatar?.status, firstKey]);
  const cast = state.inputs.cast;
  const art = useMemo(() => {
    const chars = (choices ?? []).filter(c => c.kind === 'char' && c.id != null && isArtKey(c.image));
    const head = chars.slice(0, ART),
      picked = chars.find(c => c.value === cast);
    return picked && !head.includes(picked) ? [picked, ...head] : head;
  }, [choices, cast]);
  const artKey = art.map(c => `${c.id}:${c.version}`).join(',');
  const [images, setImages] = useState<{ owner: unknown; urls: Record<number, string> }>({ owner: null, urls: {} });
  useEffect(() => {
    if (!artKey) return;
    const epoch = endpoints.sessionEpoch?.() ?? 0;
    const owner = state.ownerId;
    let live = true;
    // Ключ и загрузка — как у use-studio-artwork: blob-URL общий с витриной и другими экранами, второй раз не качается.
    const leases = artKey.split(',').map((key, index) => {
      const id = Number(key.split(':')[0]);
      const lease = leaseArtwork(epoch, key, () => endpoints.avatarCatalog.image(id), index);
      lease.url.then(
        url => {
          // Сессия сменилась (выход, слияние аккаунтов) — картинки прежнего владельца не показываем.
          if (live && (endpoints.sessionEpoch?.() ?? 0) === epoch)
            setImages(current => ({ owner, urls: { ...(current.owner === owner ? current.urls : {}), [id]: url } }));
        },
        // Картинка не пришла — в ряду остаётся буква, заказ не блокируется.
        () => {},
      );
      return lease;
    });
    return () => {
      live = false;
      leases.forEach(lease => lease.release());
    };
  }, [artKey, state.ownerId]);
  const pictured = useMemo(() => {
    if (!choices) return null;
    const urls = images.owner === state.ownerId ? images.urls : {};
    // Только удерживаемые сейчас: отпущенный blob-URL кэш может освободить.
    const held = new Set(art.map(c => c.id));
    return choices.map(c => (c.id != null && held.has(c.id) && urls[c.id] ? { ...c, image: urls[c.id] } : c));
  }, [choices, art, images, state.ownerId]);
  return { choices: pictured, error, retry: () => setAttempt(v => v + 1) };
}
